import type {
  LastVisitFilter as LastVisitFilterType,
  PatientsWaSummary,
  WaFilter,
} from '@/lib/api-client'
import type { Patient } from '@/lib/api-client'
import { exportPatientsToCSV, getExportStats } from '@/lib/export'
import type { Table } from '@tanstack/react-table'
import {
  Check,
  ChevronDown,
  Download,
  Loader2,
  MessageCircle,
  Search,
  SlidersHorizontal,
  UserMinus,
  Users,
  X,
} from 'lucide-react'
import * as React from 'react'
import { useState } from 'react'
import { toast } from 'sonner'

export type DateFilter = 'all' | 'today' | 'week' | 'month' | 'lastMonth' | 'last3Months' | 'year'

interface PatientToolbarProps<TData> {
  search: string
  onSearchChange: (value: string) => void
  /** @deprecated legacy last-visit date filter, no longer rendered */
  dateFilter?: DateFilter
  onDateFilterChange?: (value: DateFilter) => void
  lastVisitFilter: LastVisitFilterType
  onLastVisitFilterChange: (value: LastVisitFilterType) => void
  waFilter: WaFilter
  onWaFilterChange: (value: WaFilter) => void
  waSummary?: PatientsWaSummary
  table: Table<TData>
  // For export
  totalFiltered?: number
  onExportAll?: () => void
  isExporting?: boolean
}

const LAST_VISIT_OPTIONS: {
  value: LastVisitFilterType
  label: string
  icon: React.ComponentType<{ className?: string }>
  description: string
}[] = [
  { value: 'all', label: 'All Patients', icon: Users, description: 'No filter' },
  {
    value: 'active3m',
    label: 'Active (3 months)',
    icon: Users,
    description: 'Visited in last 3 months',
  },
  {
    value: 'active6m',
    label: 'Active (6 months)',
    icon: Users,
    description: 'Visited in last 6 months',
  },
  {
    value: 'active12m',
    label: 'Active (12 months)',
    icon: Users,
    description: 'Visited in last 12 months',
  },
  {
    value: 'inactive3m',
    label: 'Inactive (3+ months)',
    icon: UserMinus,
    description: 'No visit in 3+ months',
  },
  {
    value: 'inactive6m',
    label: 'Inactive (6+ months)',
    icon: UserMinus,
    description: 'No visit in 6+ months',
  },
  {
    value: 'inactive12m',
    label: 'Inactive (12+ months)',
    icon: UserMinus,
    description: 'No visit in 12+ months',
  },
]

const WA_FILTER_OPTIONS: {
  value: WaFilter
  label: string
  description: string
  divider?: boolean
}[] = [
  { value: 'any', label: 'Any', description: 'No filter' },
  { value: 'never', label: 'Never blasted', description: 'Has a mobile number, no campaign ever' },
  { value: 'last30d', label: 'Blasted in last 30 days', description: 'Skip for fatigue' },
  {
    value: 'noReply',
    label: 'Blasted, never replied',
    description: 'Received 1+, no inbound reply',
  },
  { value: 'replied', label: 'Replied before', description: 'Any inbound after a blast' },
  { value: 'optedOut', label: 'Opted out', description: 'Excluded from all blasts', divider: true },
  { value: 'noContact', label: 'No usable mobile', description: 'Empty, landline or placeholder number' },
]

const COLUMN_LABELS: Record<string, string> = {
  select: 'Select',
  pno: 'PNO',
  name: 'Name',
  nric: 'NRIC',
  phone: 'Phone',
  whatsapp: 'WhatsApp',
  waHistory: 'WhatsApp history',
  branchName: 'Branch',
  totalVisits: 'Visits',
  totalSpent: 'Spent',
  lastVisitDate: 'Last Visit',
  actions: 'Actions',
}

export function PatientToolbar<TData>({
  search,
  onSearchChange,
  lastVisitFilter,
  onLastVisitFilterChange,
  waFilter,
  onWaFilterChange,
  waSummary,
  table,
  totalFiltered,
  onExportAll,
  isExporting,
}: PatientToolbarProps<TData>) {
  const [columnsOpen, setColumnsOpen] = useState(false)
  const [lastVisitOpen, setLastVisitOpen] = useState(false)
  const [waOpen, setWaOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)

  const currentLastVisitLabel =
    LAST_VISIT_OPTIONS.find((o) => o.value === lastVisitFilter)?.label || 'All Patients'
  const currentWaLabel = WA_FILTER_OPTIONS.find((o) => o.value === waFilter)?.label || 'Any'

  // Get selected rows for export
  const selectedRows = table.getSelectedRowModel().rows
  const selectedPatients = selectedRows.map((row) => row.original as Patient)
  const selectedCount = selectedPatients.length

  // Handle export selected
  const handleExportSelected = () => {
    if (selectedCount === 0) {
      toast.error('No patients selected')
      return
    }

    const stats = getExportStats(selectedPatients)
    if (stats.withValidPhone === 0) {
      toast.error('No patients with valid phone numbers')
      return
    }

    const result = exportPatientsToCSV(selectedPatients, { format: 'full' })
    if (result.success) {
      toast.success(`Exported ${result.count} patients to ${result.filename}`)
      table.resetRowSelection()
    } else {
      toast.error('Export failed - no valid data')
    }
    setExportOpen(false)
  }

  // Handle export full (with more data)
  const handleExportSelectedFull = () => {
    if (selectedCount === 0) {
      toast.error('No patients selected')
      return
    }

    const result = exportPatientsToCSV(selectedPatients, { format: 'full' })
    if (result.success) {
      toast.success(`Exported ${result.count} patients to ${result.filename}`)
      table.resetRowSelection()
    } else {
      toast.error('Export failed - no valid data')
    }
    setExportOpen(false)
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Search Box */}
      <div className="flex items-center gap-2 bg-card rounded-full px-3.5 py-2 w-full sm:w-[250px] border border-border transition-all focus-within:ring-2 focus-within:ring-info/20">
        <Search className="w-4 h-4 text-foreground flex-shrink-0" />
        <input
          type="text"
          placeholder="Search name, PNO, NRIC, phone..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="border-none outline-none text-[13px] font-normal w-full bg-transparent text-text-dark placeholder:text-text-muted"
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-hover"
          >
            <X className="w-3.5 h-3.5 text-text-muted" />
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2.5 flex-wrap">
        {/* Last Visit Filter (Marketing) */}
        <div className="relative">
          <button
            onClick={() => setLastVisitOpen(!lastVisitOpen)}
            className={`flex items-center justify-between gap-2 rounded-full px-3.5 py-2 min-w-[160px] border transition-all hover:bg-hover ${
              lastVisitFilter !== 'all'
                ? 'bg-primary/10 border-primary/30 text-primary'
                : 'bg-card border-border'
            }`}
          >
            <div className="flex items-center gap-2">
              {lastVisitFilter.startsWith('inactive') ? (
                <UserMinus className="w-4 h-4 flex-shrink-0" />
              ) : (
                <Users className="w-4 h-4 flex-shrink-0" />
              )}
              <span className="text-[13px] font-normal truncate">{currentLastVisitLabel}</span>
            </div>
            <ChevronDown
              className={`w-4 h-4 flex-shrink-0 transition-transform ${
                lastVisitOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {lastVisitOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setLastVisitOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-[220px] rounded-lg border border-border bg-card p-1 shadow-lg z-50">
                <div className="px-2.5 py-1.5 text-[11px] font-medium text-text-muted uppercase tracking-wide">
                  Filter by Last Visit
                </div>
                {LAST_VISIT_OPTIONS.map((option) => {
                  const Icon = option.icon
                  return (
                    <button
                      key={option.value}
                      onClick={() => {
                        onLastVisitFilterChange(option.value)
                        setLastVisitOpen(false)
                      }}
                      className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-[13px] transition-colors ${
                        lastVisitFilter === option.value
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-text-dark hover:bg-hover'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <div className="flex-1 text-left">
                        <div>{option.label}</div>
                        <div className="text-[11px] text-text-muted">{option.description}</div>
                      </div>
                      {lastVisitFilter === option.value && (
                        <Check className="w-3.5 h-3.5 text-primary" />
                      )}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* WhatsApp campaign history filter */}
        <div className="relative">
          <button
            onClick={() => setWaOpen(!waOpen)}
            className={`flex items-center justify-between gap-2 rounded-full px-3.5 py-2 min-w-[150px] border transition-all hover:bg-hover ${
              waFilter !== 'any'
                ? 'bg-primary/10 border-primary/30 text-primary'
                : 'bg-card border-border'
            }`}
          >
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-[13px] font-normal truncate">WhatsApp: {currentWaLabel}</span>
            </div>
            <ChevronDown
              className={`w-4 h-4 flex-shrink-0 transition-transform ${waOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {waOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setWaOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-[260px] rounded-lg border border-border bg-card p-1 shadow-lg z-50">
                <div className="px-2.5 py-1.5 text-[11px] font-medium text-text-muted uppercase tracking-wide">
                  Filter by campaign history
                </div>
                {WA_FILTER_OPTIONS.map((option) => (
                  <React.Fragment key={option.value}>
                    {option.divider && <div className="my-1 border-t border-border" />}
                    <button
                      onClick={() => {
                        onWaFilterChange(option.value)
                        setWaOpen(false)
                      }}
                      className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-[13px] transition-colors ${
                        waFilter === option.value
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-text-dark hover:bg-hover'
                      }`}
                    >
                      <div className="flex-1 text-left">
                        <div>{option.label}</div>
                        <div className="text-[11px] text-text-muted font-normal">
                          {option.description}
                        </div>
                      </div>
                      {waSummary && (
                        <span className="font-mono text-[11px] text-text-muted">
                          {waSummary[option.value].toLocaleString()}
                        </span>
                      )}
                      {waFilter === option.value && <Check className="w-3.5 h-3.5 text-primary" />}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Export Button */}
        <div className="relative">
          <button
            onClick={() => setExportOpen(!exportOpen)}
            className="flex items-center justify-between gap-2 bg-green-600 hover:bg-green-700 text-white rounded-full px-3.5 py-2 transition-all"
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span className="text-[13px] font-medium">Export</span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${exportOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {exportOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setExportOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-[240px] rounded-lg border border-border bg-card p-1 shadow-lg z-50">
                <div className="px-2.5 py-1.5 text-[11px] font-medium text-text-muted uppercase tracking-wide">
                  Export for WhatsApp
                </div>

                {/* Export Selected */}
                <button
                  onClick={handleExportSelected}
                  disabled={selectedCount === 0}
                  className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-[13px] transition-colors ${
                    selectedCount > 0
                      ? 'text-text-dark hover:bg-hover'
                      : 'text-text-muted cursor-not-allowed'
                  }`}
                >
                  <Download className="w-4 h-4" />
                  <div className="flex-1 text-left">
                    <div>Export Selected</div>
                    <div className="text-[11px] text-text-muted">
                      {selectedCount > 0 ? `${selectedCount} patients` : 'Select rows first'}
                    </div>
                  </div>
                </button>

                {/* Export All Filtered */}
                <button
                  onClick={() => {
                    if (onExportAll) {
                      onExportAll()
                      setExportOpen(false)
                    }
                  }}
                  disabled={isExporting}
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-[13px] text-text-dark hover:bg-hover transition-colors"
                >
                  {isExporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <div className="flex-1 text-left">
                    <div>Export All Filtered</div>
                    <div className="text-[11px] text-text-muted">
                      {totalFiltered !== undefined
                        ? `${totalFiltered.toLocaleString()} patients`
                        : 'All matching filters'}
                    </div>
                  </div>
                </button>

                <div className="my-1 border-t border-border" />

                <div className="px-2.5 py-1.5 text-[11px] text-text-muted">
                  CSV format for WhatsApp Business API
                </div>
              </div>
            </>
          )}
        </div>

        {/* Column Selector */}
        <div className="relative">
          <button
            onClick={() => setColumnsOpen(!columnsOpen)}
            className="flex items-center justify-between gap-2 bg-card rounded-full px-3.5 py-2 w-[117px] border border-border transition-all hover:bg-hover"
          >
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-foreground flex-shrink-0" />
              <span className="text-[13px] font-normal text-text-dark">Columns</span>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-foreground flex-shrink-0 transition-transform ${
                columnsOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {columnsOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setColumnsOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-[162px] rounded-lg border border-border bg-card p-1 shadow-lg z-50">
                <div className="px-2.5 py-1.5 text-[11px] font-medium text-text-muted uppercase tracking-wide">
                  Toggle columns
                </div>
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => (
                    <button
                      key={column.id}
                      onClick={() => column.toggleVisibility(!column.getIsVisible())}
                      className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                        column.getIsVisible() ? 'text-text-dark' : 'text-text-muted'
                      } hover:bg-hover`}
                    >
                      {COLUMN_LABELS[column.id] || column.id}
                      {column.getIsVisible() && <Check className="w-3.5 h-3.5 text-success" />}
                    </button>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
